import {ExpressionId} from '@frontapp/workflows/expressions';
import {Dispatch, SetStateAction, useCallback, useReducer, useState} from 'react';

import {
  ExpressionAction,
  ExpressionActionTypesEnum,
} from '../../../settings/editors/expressions/expressionActions';
import {Expression, PreExpression} from '../../../settings/editors/expressions/expressionModel';
import {expressionReducer} from '../../../settings/editors/expressions/expressionReducer';
import {
  createExpressionsContext,
  ExpressionCreationState,
} from '../../../settings/editors/expressions/expressionsContext';
import {
  ExpressionDependencies,
  ExpressionState,
  ExpressionStateActions,
  useExpressionState,
} from '../../../settings/editors/expressions/expressionState';
import {InferredType} from '../../../settings/editors/expressions/infer/expressionResultModel';
import {FlowBuilderExpressionsEditor} from './expressionHelpers';

/*
 * Hooks.
 */

export interface FlowBuilderExpressionsValidator {
  onExpressionUpdated: (expression: Expression) => void;
  onExpressionDeleted: (expressionId: ExpressionId) => void;
}

export function useFlowBuilderExpressionsEditor(
  initialExpressions: ReadonlyArray<PreExpression>,
  initialInfer: InferredType,
  dependencies: ExpressionDependencies,
  expressionsValidator?: FlowBuilderExpressionsValidator,
): FlowBuilderExpressionsEditor {
  const {state, actions} = useFlowBuilderExpressionsEditorState(
    initialExpressions,
    initialInfer,
    dependencies,
    expressionsValidator,
  );

  const [expressionCreationState, setExpressionCreationState] = useState<ExpressionCreationState>();
  const context = useExpressionEditorContext(
    state,
    dependencies,
    actions,
    initialInfer,
    expressionCreationState,
    setExpressionCreationState,
  );

  const openCreateExpression = useCallback(() => actions.onSetPanelOpen(true), [actions]);

  const [isExpressionListPanelVisible, setIsExpressionListPanelVisible] = useState(false);
  const openExpressionList = () => setIsExpressionListPanelVisible(true);
  const closeExpressionList = () => setIsExpressionListPanelVisible(false);

  return {
    state,
    actions,
    context,
    openCreateExpression,
    isExpressionListPanelVisible,
    openExpressionList,
    closeExpressionList,
  };
}

function useFlowBuilderExpressionsEditorState(
  preExpressions: ReadonlyArray<PreExpression>,
  initialInfer: InferredType,
  dependencies: ExpressionDependencies,
  expressionsValidator?: FlowBuilderExpressionsValidator,
) {
  const flowBuilderExpressionReducer = (
    state: ExpressionState,
    action: ExpressionAction<ExpressionState>,
  ) => {
    if (expressionsValidator) {
      switch (action.type) {
        case ExpressionActionTypesEnum.SAVE_DRAFT_EXPRESSION: {
          if (state.expressionPanelState.selectedExpression) {
            expressionsValidator.onExpressionUpdated(state.expressionPanelState.selectedExpression);
          }
          break;
        }
        case ExpressionActionTypesEnum.DELETE_EXPRESSION: {
          expressionsValidator.onExpressionDeleted(action.expressionId);
          break;
        }

        default:
          break;
      }
    }

    return expressionReducer(state, action, dependencies);
  };

  const [state, dispatch] = useReducer(
    flowBuilderExpressionReducer,
    ExpressionState.createInitialState(preExpressions, initialInfer, dependencies),
  );

  const actions = useExpressionState<ExpressionState>({}, state, dispatch);

  return {state, actions};
}

function useExpressionEditorContext(
  state: ExpressionState,
  dependencies: ExpressionDependencies,
  actions: ExpressionStateActions,
  initialInfer: InferredType,
  expressionCreationState: ExpressionCreationState | undefined,
  setExpressionCreationState: Dispatch<SetStateAction<ExpressionCreationState | undefined>>,
) {
  return createExpressionsContext({
    expressions: state.expressions,
    dependencies,
    initialInfer,
    setPanelOpen: () => actions.onSetPanelOpen(true),
    selectExpression: actions.onSelectExpression,
    replaceExpressions: actions.onReplaceExpressions,
    expressionCreationState,
    setExpressionCreationState,
  });
}
